import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import SupportChatWidget from "../support/SupportChatWidget";
import { useEmailCompose } from "../../contexts/EmailComposeContext";
import { useRunningTimer } from "../../contexts/RunningTimerContext";
import { useUserDataContext } from "../../context/UserDataContext";
import { useSoftPhoneHeight } from "../messages/softPhoneStore";
import AssistantLauncher from "./AssistantLauncher";
import AssistantPanel from "./AssistantPanel";
import { useAssistantChat } from "./useAssistantChat";
import { useRegisteredAssistantContext } from "./assistantContextStore";
import { entityFromPath, isHiddenPath, pageKindFor, suggestionsFor } from "./pageContext";
import type { AssistantEntity } from "./types";

const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 639px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
};

/**
 * Global assistant: launcher, panel and the (controlled) support widget.
 * Mounted once inside the Router for signed-in, onboarded users.
 */
const AssistantHost: React.FC = () => {
  const location = useLocation();
  const hidden = isHiddenPath(location.pathname);
  if (hidden) return null;
  return <AssistantHostInner />;
};

const AssistantHostInner: React.FC = () => {
  const location = useLocation();
  const { mode: composeMode } = useEmailCompose();
  const { runningTimer } = useRunningTimer();
  const { userData, firmData, canManageBilling } = useUserDataContext();
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const chat = useAssistantChat();

  // ── Page context ──
  const registered = useRegisteredAssistantContext();
  const urlEntity = useMemo(() => entityFromPath(location.pathname), [location.pathname]);
  const entity: AssistantEntity | null = useMemo(() => {
    if (registered && (!urlEntity || urlEntity.id === registered.id)) return registered;
    return urlEntity;
  }, [registered, urlEntity]);
  const entityKey = entity ? `${entity.type}:${entity.id}` : null;
  const activeEntity = entity && entityKey !== dismissedKey ? entity : null;

  useEffect(() => {
    // A new record brings the chip back
    setDismissedKey((k) => (k && k !== entityKey ? null : k));
  }, [entityKey]);

  const suggestions = useMemo(
    () => suggestionsFor(pageKindFor(location.pathname, entity)),
    [location.pathname, entity]
  );

  const canSeeEveryone =
    (!!userData?._id && userData._id === firmData?.adminUserId) ||
    userData?.role === "owner" ||
    userData?.role === "admin";

  // ── Open / close ──
  const openPanel = useCallback(() => {
    restoreFocusRef.current = (document.activeElement as HTMLElement) || null;
    setSupportOpen(false);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    const target = restoreFocusRef.current;
    requestAnimationFrame(() => {
      if (target && document.contains(target) && target !== document.body) target.focus();
      else launcherRef.current?.focus();
    });
  }, []);

  const toggle = useCallback(() => (open ? closePanel() : openPanel()), [open, openPanel, closePanel]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === "j" || e.key === "J")) {
        e.preventDefault();
        toggle();
        return;
      }
      if (e.key === "Escape" && open && !e.defaultPrevented) {
        // Leave Escape to any modal stacked above the panel
        const target = e.target as Node | null;
        const inPanel = !!(target && panelRef.current?.contains(target));
        const onLauncher = !!(target && launcherRef.current?.contains(target));
        if (target && target !== document.body && !inPanel && !onLauncher) return;
        closePanel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, open, closePanel]);

  const openSupport = useCallback(() => {
    setOpen(false);
    setSupportOpen(true);
  }, []);

  const send = useCallback(
    (text: string) => {
      chat.send(text, {
        path: `${location.pathname}${location.search}`,
        title: typeof document !== "undefined" ? document.title : undefined,
        entity: activeEntity || undefined,
      });
    },
    [chat, location.pathname, location.search, activeEntity]
  );

  // The email compose popup sits in the same corner; hide the launcher while it is open
  const composeOpen = composeMode !== "closed";
  const timerBarVisible = isMobile && !!runningTimer;

  // During a call the SoftPhone card holds the bottom-right corner (bottom-6 right-6):
  // lift the launcher above it and keep the panel clear of it.
  const callCardPx = useSoftPhoneHeight();
  const panelStyle: React.CSSProperties = { transformOrigin: "bottom right" };
  if (callCardPx > 0) {
    // Mobile: panel is full screen and the launcher is hidden. Desktop: clear the lifted launcher too.
    const bottom = isMobile ? `calc(2.25rem + ${callCardPx}px)` : `calc(6.5rem + ${callCardPx}px)`;
    panelStyle.bottom = bottom;
    if (!isMobile) panelStyle.height = `min(78vh, 720px, calc(100vh - ${bottom} - 1.5rem))`;
  }

  return (
    <>
      <AnimatePresence>
        {!composeOpen && (
          <AssistantLauncher
            key="assistant-launcher"
            ref={launcherRef}
            open={open}
            unread={supportUnread}
            raised={timerBarVisible}
            liftPx={callCardPx}
            onClick={toggle}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key="assistant-panel"
            ref={panelRef}
            role="dialog"
            aria-modal={isMobile ? true : undefined}
            aria-label="Kavoa assistant"
            initial={isMobile ? { opacity: 0, y: 40 } : { opacity: 0, y: 24, scale: 0.94 }}
            animate={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0, scale: 1 }}
            exit={isMobile ? { opacity: 0, y: 40 } : { opacity: 0, y: 24, scale: 0.94 }}
            transition={{ type: "spring", damping: 28, stiffness: 360 }}
            style={panelStyle}
            className="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-white sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[min(78vh,720px)] sm:w-[420px] sm:max-w-[calc(100vw-3rem)] sm:rounded-2xl sm:border sm:border-slate-200 sm:shadow-[0_25px_65px_rgba(0,0,0,0.15),0_8px_20px_rgba(0,0,0,0.08)]"
          >
            <AssistantPanel
              chat={chat}
              entity={activeEntity}
              onDropEntity={() => setDismissedKey(entityKey)}
              suggestions={suggestions}
              onSend={send}
              onClose={closePanel}
              onOpenSupport={openSupport}
              supportUnread={supportUnread}
              canSeeEveryone={canSeeEveryone}
              canManageBilling={canManageBilling}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <SupportChatWidget
        hideLauncher
        open={supportOpen}
        onOpenChange={setSupportOpen}
        onUnreadChange={setSupportUnread}
      />
    </>
  );
};

export default AssistantHost;
